import { useCallback, useEffect, useState, useRef } from "react";
import {
  useCollaborationStore,
  DragConflict,
  ConflictChoice,
} from "@/stores/collaboration";
import { logger } from "@/utils";

// Enhanced conflict types for broader collaboration conflicts
interface OperationalConflict {
  id: string;
  type:
    | "simultaneous_edit"
    | "delete_modify"
    | "move_modify"
    | "property_conflict"
    | "structural_conflict"
    | "dependency_conflict";
  severity: "low" | "medium" | "high" | "critical";
  operation1: Operation;
  operation2: Operation;
  conflictingField?: string;
  description: string;
  resolutionOptions: ConflictResolutionOption[];
  autoResolvable: boolean;
  suggestedResolution?: ConflictResolutionOption;
  createdAt: Date;
  projectId: string;
  affectedUsers: string[];
  position?: { x: number; y: number };
}

interface Operation {
  id: string;
  type: string;
  componentId?: string;
  field?: string;
  value?: any;
  oldValue?: any;
  userId: string;
  timestamp: Date;
  vector: { [userId: string]: number };
  dependencies: string[];
}

interface ConflictResolutionOption {
  id: string;
  type: "accept_local" | "accept_remote" | "merge" | "custom";
  title: string;
  description: string;
  preview?: any;
  confidence: number;
}

export interface ConflictResolutionState {
  activeConflicts: DragConflict[];
  operationalConflicts: OperationalConflict[];
  conflictChoices: ConflictChoice[];
  hasActiveConflicts: boolean;
  autoResolveTimers: { [conflictId: string]: number };
  conflictStats: {
    totalResolved: number;
    automaticResolutions: number;
    userChoiceResolutions: number;
    manualMergeResolutions: number;
    averageResolutionTime: number;
  };
}

export interface ConflictResolutionActions {
  createConflict: (componentId: string, userIds: string[]) => string;
  resolveConflict: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
    resolvedBy?: string,
  ) => void;
  resolveOperationalConflict: (
    conflictId: string,
    optionId: string,
    customData?: any,
  ) => Promise<boolean>;
  addChoice: (choice: Omit<ConflictChoice, "id" | "timestamp">) => void;
  resolveChoice: (choiceId: string) => void;
  dismissConflict: (conflictId: string) => void;
  getConflictsForComponent: (componentId: string) => DragConflict[];
  getOperationalConflictsForComponent: (
    componentId: string,
  ) => OperationalConflict[];
  getVisualConflicts: () => Array<{
    id: string;
    type: string;
    severity: string;
    componentId: string;
    position: { x: number; y: number };
    affectedUsers: string[];
    autoResolveTime?: number;
  }>;
  getResolutionRecommendation: (conflictId: string) => {
    resolution: "merge" | "reject" | "queue";
    confidence: number;
    reason: string;
  };
  startAutoResolutionTimer: (conflictId: string, delay: number) => void;
  cancelAutoResolution: (conflictId: string) => void;
}

export interface ConflictResolutionOptions {
  enableAutoResolution?: boolean;
  autoResolutionDelay?: number; // seconds
  confidenceThreshold?: number; // 0-1, for automatic resolution
  maxConflictAge?: number; // milliseconds before auto-cleanup
}

const defaultOptions: ConflictResolutionOptions = {
  enableAutoResolution: true,
  autoResolutionDelay: 5, // 5 seconds
  confidenceThreshold: 0.9, // 90% confidence for auto-resolution
  maxConflictAge: 5 * 60 * 1000, // 5 minutes
};

export const useConflictResolution = (
  options: ConflictResolutionOptions = {},
) => {
  const opts = { ...defaultOptions, ...options };

  const {
    dragConflicts,
    conflictChoices,
    hasActiveConflicts,
    stats,
    createDragConflict,
    resolveDragConflict,
    addConflictChoice,
    resolveConflictChoice,
    getConflictsForComponent,
    clearExpiredConflicts,
  } = useCollaborationStore();

  // Auto-resolution timers
  const autoResolutionTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const resolveConflictRef = useRef<
    | ((
        conflictId: string,
        resolution: "merge" | "reject" | "queue",
        resolvedBy?: string,
      ) => void)
    | null
  >(null);

  // Enhanced operational conflicts
  const [operationalConflicts, setOperationalConflicts] = useState<
    OperationalConflict[]
  >([]);
  const [autoResolveCountdowns, setAutoResolveCountdowns] = useState<{
    [conflictId: string]: number;
  }>({});

  // Resolution statistics
  const [resolutionStats, setResolutionStats] = useState({
    totalResolved: 0,
    automaticResolutions: 0,
    userChoiceResolutions: 0,
    manualMergeResolutions: 0,
    averageResolutionTime: 0,
    resolutionTimes: [] as number[],
  });

  /**
   * Create a new conflict
   */
  const createConflict = useCallback(
    (componentId: string, userIds: string[]): string => {
      const conflictId = createDragConflict(componentId, userIds);

      // Create automatic resolution choice if enabled
      if (opts.enableAutoResolution) {
        const recommendation = getResolutionRecommendation(conflictId);

        if (recommendation.confidence >= opts.confidenceThreshold!) {
          addConflictChoice({
            type: "automatic",
            conflictId,
            description: `Automatic resolution: ${recommendation.reason}`,
            autoResolveIn: opts.autoResolutionDelay,
          });

          // Start auto-resolution timer
          startAutoResolutionTimer(conflictId, opts.autoResolutionDelay!);
        } else {
          // Create user choice
          addConflictChoice({
            type: "user_choice",
            conflictId,
            description:
              "Multiple users are editing the same component. How would you like to resolve this?",
            options: [
              {
                label: "Merge Changes",
                action: () => resolveConflict(conflictId, "merge"),
                variant: "primary",
              },
              {
                label: "Keep My Changes",
                action: () => resolveConflict(conflictId, "reject"),
                variant: "secondary",
              },
              {
                label: "Queue Changes",
                action: () => resolveConflict(conflictId, "queue"),
                variant: "secondary",
              },
            ],
          });
        }
      }

      logger.info("Conflict created", { conflictId, componentId, userIds });
      return conflictId;
    },
    [createDragConflict, opts, addConflictChoice],
  );

  /**
   * Cancel auto-resolution
   */
  const cancelAutoResolution = useCallback((conflictId: string) => {
    const timer = autoResolutionTimers.current.get(conflictId);
    if (timer) {
      clearTimeout(timer);
      autoResolutionTimers.current.delete(conflictId);
    }
  }, []);

  /**
   * Get resolution recommendation using AI-like heuristics
   */
  const getResolutionRecommendation = useCallback(
    (conflictId: string) => {
      const conflict = dragConflicts.get(conflictId);
      if (!conflict) {
        return {
          resolution: "reject" as const,
          confidence: 0,
          reason: "Conflict not found",
        };
      }

      const { users, componentId } = conflict;

      // Heuristic 1: If one user has been more active recently, prefer their changes
      const sortedUsers = users.sort(
        (a, b) => b.lastActivity.getTime() - a.lastActivity.getTime(),
      );

      const mostRecentUser = sortedUsers[0];
      const timeDifference =
        mostRecentUser.lastActivity.getTime() -
        (sortedUsers[1]?.lastActivity.getTime() || 0);

      if (timeDifference > 10000) {
        // 10 seconds difference
        return {
          resolution: "merge" as const,
          confidence: 0.8,
          reason: `User ${mostRecentUser.user.username} has been more active recently`,
        };
      }

      // Heuristic 2: If conflict involves a dragging operation, queue it
      const hasDraggingUser = users.some(
        (user) => user.currentAction === "dragging",
      );
      if (hasDraggingUser) {
        return {
          resolution: "queue" as const,
          confidence: 0.7,
          reason: "Detected drag operation, queueing for sequential resolution",
        };
      }

      // Heuristic 3: Simple properties can usually be merged
      const isSimpleProperty = Math.random() > 0.3; // Placeholder - would analyze actual changes
      if (isSimpleProperty) {
        return {
          resolution: "merge" as const,
          confidence: 0.9,
          reason: "Simple property changes can be safely merged",
        };
      }

      // Default: Low confidence, let user decide
      return {
        resolution: "reject" as const,
        confidence: 0.3,
        reason: "Complex changes detected, user decision required",
      };
    },
    [dragConflicts],
  );

  /**
   * Start auto-resolution timer
   */
  const startAutoResolutionTimer = useCallback(
    (conflictId: string, delay: number) => {
      // Cancel existing timer if any
      cancelAutoResolution(conflictId);

      const timer = setTimeout(() => {
        const recommendation = getResolutionRecommendation(conflictId);
        // We'll call resolveConflict through the current reference to avoid circular dependency
        const currentResolveConflict = resolveConflictRef.current;
        if (currentResolveConflict) {
          currentResolveConflict(conflictId, recommendation.resolution, "auto");
        }
        autoResolutionTimers.current.delete(conflictId);
      }, delay * 1000);

      autoResolutionTimers.current.set(conflictId, timer);
    },
    [getResolutionRecommendation, cancelAutoResolution],
  );

  /**
   * Resolve a conflict
   */
  const resolveConflict = useCallback(
    (
      conflictId: string,
      resolution: "merge" | "reject" | "queue",
      resolvedBy?: string,
    ) => {
      const conflict = dragConflicts.get(conflictId);
      if (!conflict) return;

      const startTime = conflict.timestamp.getTime();
      const resolutionTime = Date.now() - startTime;

      // Cancel any auto-resolution timer
      cancelAutoResolution(conflictId);

      // Resolve the conflict
      resolveDragConflict(conflictId, resolution, resolvedBy);

      // Update statistics
      setResolutionStats((prev) => {
        const newResolutionTimes = [
          ...prev.resolutionTimes,
          resolutionTime,
        ].slice(-100); // Keep last 100
        const averageResolutionTime =
          newResolutionTimes.reduce((a, b) => a + b, 0) /
          newResolutionTimes.length;

        return {
          totalResolved: prev.totalResolved + 1,
          automaticResolutions:
            prev.automaticResolutions + (resolvedBy === "auto" ? 1 : 0),
          userChoiceResolutions:
            prev.userChoiceResolutions + (resolvedBy !== "auto" ? 1 : 0),
          manualMergeResolutions:
            prev.manualMergeResolutions + (resolution === "merge" ? 1 : 0),
          averageResolutionTime,
          resolutionTimes: newResolutionTimes,
        };
      });

      logger.info("Conflict resolved", {
        conflictId,
        resolution,
        resolvedBy,
        resolutionTime,
      });
    },
    [dragConflicts, resolveDragConflict, cancelAutoResolution],
  );

  // Update the ref to point to the current resolveConflict function
  resolveConflictRef.current = resolveConflict;

  /**
   * Add a conflict choice
   */
  const addChoice = useCallback(
    (choice: Omit<ConflictChoice, "id" | "timestamp">) => {
      addConflictChoice(choice);
    },
    [addConflictChoice],
  );

  /**
   * Resolve a conflict choice
   */
  const resolveChoice = useCallback(
    (choiceId: string) => {
      resolveConflictChoice(choiceId);
    },
    [resolveConflictChoice],
  );

  /**
   * Auto-cleanup expired conflicts
   */
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      clearExpiredConflicts();

      // Clean up expired auto-resolution timers
      const now = Date.now();
      for (const [
        conflictId,
        timer,
      ] of autoResolutionTimers.current.entries()) {
        const conflict = dragConflicts.get(conflictId);
        if (
          !conflict ||
          now - conflict.timestamp.getTime() > opts.maxConflictAge!
        ) {
          clearTimeout(timer);
          autoResolutionTimers.current.delete(conflictId);
        }
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, [clearExpiredConflicts, dragConflicts, opts.maxConflictAge]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      autoResolutionTimers.current.forEach((timer) => clearTimeout(timer));
      autoResolutionTimers.current.clear();
    };
  }, []);

  /**
   * Auto-resolve conflicts with high confidence
   */
  useEffect(() => {
    if (!opts.enableAutoResolution) return;

    for (const [conflictId, conflict] of dragConflicts.entries()) {
      if (conflict.status !== "active") continue;

      // Check if we already have a timer for this conflict
      if (autoResolutionTimers.current.has(conflictId)) continue;

      // Check if there's already a choice for this conflict
      const hasChoice = conflictChoices.some(
        (choice) => choice.conflictId === conflictId,
      );
      if (hasChoice) continue;

      const recommendation = getResolutionRecommendation(conflictId);

      if (recommendation.confidence >= opts.confidenceThreshold!) {
        addChoice({
          type: "automatic",
          conflictId,
          description: `Auto-resolving: ${recommendation.reason}`,
          autoResolveIn: opts.autoResolutionDelay,
        });

        startAutoResolutionTimer(conflictId, opts.autoResolutionDelay!);
      }
    }
  }, [
    dragConflicts,
    conflictChoices,
    opts,
    addChoice,
    getResolutionRecommendation,
    startAutoResolutionTimer,
  ]);

  /**
   * Resolve operational conflict
   */
  const resolveOperationalConflict = useCallback(
    async (
      conflictId: string,
      optionId: string,
      customData?: any,
    ): Promise<boolean> => {
      try {
        // Remove from operational conflicts
        setOperationalConflicts((prev) =>
          prev.filter((c) => c.id !== conflictId),
        );

        // Clear auto-resolve countdown
        setAutoResolveCountdowns((prev) => {
          const newCountdowns = { ...prev };
          delete newCountdowns[conflictId];
          return newCountdowns;
        });

        logger.info("Operational conflict resolved", { conflictId, optionId });
        return true;
      } catch (error) {
        logger.error("Error resolving operational conflict:", error);
        return false;
      }
    },
    [],
  );

  /**
   * Dismiss conflict
   */
  const dismissConflict = useCallback((conflictId: string) => {
    setOperationalConflicts((prev) => prev.filter((c) => c.id !== conflictId));
    setAutoResolveCountdowns((prev) => {
      const newCountdowns = { ...prev };
      delete newCountdowns[conflictId];
      return newCountdowns;
    });
  }, []);

  /**
   * Get operational conflicts for component
   */
  const getOperationalConflictsForComponent = useCallback(
    (componentId: string) => {
      return operationalConflicts.filter(
        (conflict) =>
          conflict.operation1.componentId === componentId ||
          conflict.operation2.componentId === componentId,
      );
    },
    [operationalConflicts],
  );

  /**
   * Get conflicts for visual display
   */
  const getVisualConflicts = useCallback(() => {
    return operationalConflicts.map((conflict) => ({
      id: conflict.id,
      type: conflict.type,
      severity: conflict.severity,
      componentId: conflict.operation1.componentId || "",
      position: conflict.position || { x: 100, y: 100 },
      affectedUsers: conflict.affectedUsers,
      autoResolveTime: autoResolveCountdowns[conflict.id],
    }));
  }, [operationalConflicts, autoResolveCountdowns]);

  const activeConflicts = Array.from(dragConflicts.values()).filter(
    (conflict) => conflict.status === "active",
  );

  const state: ConflictResolutionState = {
    activeConflicts,
    operationalConflicts,
    conflictChoices: [...conflictChoices],
    hasActiveConflicts: hasActiveConflicts() || operationalConflicts.length > 0,
    autoResolveTimers: autoResolveCountdowns,
    conflictStats: {
      ...resolutionStats,
      totalResolved: stats.conflictsResolved + resolutionStats.totalResolved,
    },
  };

  const actions: ConflictResolutionActions = {
    createConflict,
    resolveConflict,
    resolveOperationalConflict,
    addChoice,
    resolveChoice,
    dismissConflict,
    getConflictsForComponent,
    getOperationalConflictsForComponent,
    getVisualConflicts,
    getResolutionRecommendation,
    startAutoResolutionTimer,
    cancelAutoResolution,
  };

  return { ...state, ...actions };
};
